import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Link, useLocation } from 'react-router-dom';
import { Layout, Menu, Typography, Space, Badge } from 'antd';
import {
  DashboardOutlined, UserOutlined, SafetyCertificateOutlined,
  UnorderedListOutlined, DollarOutlined, AppstoreOutlined,
  SettingOutlined, LineChartOutlined, WalletOutlined, RobotOutlined,
  AuditOutlined, FileTextOutlined, TeamOutlined,
} from '@ant-design/icons';
import API from './api';
import AiDispatcher from './pages/AiDispatcher';
import Dashboard from './pages/Dashboard';
import Users from './pages/Users';
import Providers from './pages/Providers';
import Bookings from './pages/Bookings';
import Payments from './pages/Payments';
import Payouts from './pages/Payouts';
import Categories from './pages/Categories';
import Verification from './pages/Verification';
import Config from './pages/Config';
import Analytics from './pages/Analytics';
import FeeConfiguration from './pages/FeeConfiguration';
import KycDashboardPage from './pages/kyc/KycDashboardPage';
import KycQueuePage from './pages/kyc/KycQueuePage';
import ProviderOnboardingDetailPage from './pages/kyc/ProviderOnboardingDetailPage';
import AdminManagementPage from './pages/admin/AdminManagementPage';
import AdminAuditLogPage from './pages/audit/AdminAuditLogPage';
import AcceptInvitePage from './pages/auth/AcceptInvitePage';
import MfaSetupPage from './pages/auth/MfaSetupPage';

const { Sider, Content, Header } = Layout;

function AppLayout() {
  const location = useLocation();
  const [pendingCount, setPendingCount] = useState(0);

  useEffect(() => {
    const fetchPending = () => {
      API.get('/payouts/pending')
        .then(r => setPendingCount((r.data || []).length))
        .catch(() => {});
    };
    fetchPending();
    const t = setInterval(fetchPending, 60_000);
    return () => clearInterval(t);
  }, []);

  const NAV = [
    { key: '/',             icon: <DashboardOutlined />,        label: 'Dashboard' },
    { key: '/users',        icon: <UserOutlined />,             label: 'Users' },
    { key: '/providers',    icon: <SafetyCertificateOutlined />, label: 'Providers' },
    { key: '/verification', icon: <SafetyCertificateOutlined />, label: 'Verification' },
    { key: '/bookings',     icon: <UnorderedListOutlined />,    label: 'Bookings' },
    { key: '/payments',     icon: <DollarOutlined />,           label: 'Treasury' },
    {
      key: '/payouts',
      icon: <WalletOutlined />,
      label: (
        <Space>
          Payout Requests
          {pendingCount > 0 && <Badge count={pendingCount} style={{ backgroundColor: '#f5222d' }} />}
        </Space>
      ),
    },
    { key: '/categories',    icon: <AppstoreOutlined />,  label: 'Categories' },
    { key: '/ai-dispatcher', icon: <RobotOutlined />,     label: 'AI Dispatcher' },
    { key: '/analytics',     icon: <LineChartOutlined />, label: 'Analytics' },
    { key: '/config',        icon: <SettingOutlined />,   label: 'Control Room' },
    { key: '/fees',          icon: <SettingOutlined />,   label: 'Fee Configuration' },
    { key: '/kyc',           icon: <AuditOutlined />,     label: 'KYC' },
    { key: '/admin/management', icon: <TeamOutlined />,   label: 'Admins' },
    { key: '/admin/audit-log',  icon: <FileTextOutlined />, label: 'Audit Log' },
  ];

  // Match selected key — prefer exact, then prefix for sub-paths
  const selectedKey = NAV.find(n => location.pathname === n.key)?.key
    ?? NAV.find(n => n.key !== '/' && location.pathname.startsWith(n.key))?.key
    ?? '/';

  const currentNavItem = NAV.find(n => n.key === selectedKey);
  const displayLabel =
    currentNavItem && typeof currentNavItem.label === 'string'
      ? currentNavItem.label
      : 'Admin';

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider theme="dark" width={220}>
        <div style={{ padding: '20px 24px', borderBottom: '1px solid #1f2937' }}>
          <Space align="center">
            <img src="/tap2serve.png" alt="Tap2Serve Logo" style={{ width: 24, height: 24 }} />
            <Typography.Title level={4} style={{ color: '#fff', margin: 0 }}>
              Tap2Serve Admin
            </Typography.Title>
          </Space>
          <Typography.Text style={{ color: '#64748b', fontSize: 12, display: 'block', marginTop: 4 }}>Command Center</Typography.Text>
        </div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[selectedKey]}
          items={NAV.map(n => ({ ...n, label: <Link to={n.key}>{n.label}</Link> }))}
          style={{ marginTop: 8 }}
        />
      </Sider>
      <Layout>
        <Header style={{ background: '#fff', padding: '0 24px', borderBottom: '1px solid #f0f0f0' }}>
          <Typography.Text strong style={{ fontSize: 16 }}>
            {displayLabel}
          </Typography.Text>
        </Header>
        <Content style={{ margin: 24, background: '#f5f5f5', borderRadius: 8 }}>
          <Routes>
            <Route path="/"              element={<Dashboard />} />
            <Route path="/users"         element={<Users />} />
            <Route path="/providers"     element={<Providers />} />
            <Route path="/bookings"      element={<Bookings />} />
            <Route path="/payments"      element={<Payments />} />
            <Route path="/payouts"       element={<Payouts />} />
            <Route path="/categories"    element={<Categories />} />
            <Route path="/verification"  element={<Verification />} />
            <Route path="/config"        element={<Config />} />
            <Route path="/fees"          element={<FeeConfiguration />} />
            <Route path="/analytics"     element={<Analytics />} />
            <Route path="/ai-dispatcher" element={<AiDispatcher />} />
            <Route path="/kyc"           element={<KycDashboardPage />} />
            <Route path="/kyc/queue"     element={<KycQueuePage />} />
            <Route path="/admin/providers/:providerId/onboarding" element={<ProviderOnboardingDetailPage />} />
            <Route path="/admin/management" element={<AdminManagementPage />} />
            <Route path="/admin/audit-log"  element={<AdminAuditLogPage />} />
          </Routes>
        </Content>
      </Layout>
    </Layout>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/accept-invite" element={<AcceptInvitePage />} />
        <Route path="/mfa-setup" element={<MfaSetupPage />} />
        <Route path="*" element={<AppLayout />} />
      </Routes>
    </BrowserRouter>
  );
}
